/**
 * Resumable document upload queue (per Resumable_Document_Upload_Prompts.md).
 *
 * Survives page reloads via IndexedDB for batch + row metadata. File handles
 * are kept in an in-memory Map only — browsers don't let us round-trip a
 * File reference across reloads. Rows whose handle is missing on resume
 * flip to `pending-needs-file` so the UX layer (Prompt 3) can prompt the
 * user to re-select.
 *
 * Persistence is split behind a small KvStore interface so vitest can
 * exercise the queue without a polyfill. The IndexedDB implementation
 * uses a hand-rolled wrapper — keeping it inline beats pulling in
 * idb-keyval for ~80 LOC of glue.
 */

/* ── Types ──────────────────────────────────────────────────────────── */

export interface UploadBatch {
  id: string;
  siteId: string;
  reportId?: string;
  createdAt: string;
  totalFiles: number;
  label?: string;
  /** Set by pauseBatch/resumeBatch; processBatch checks this before pulling
   *  new work. Already-running rows finish on whatever they're doing. */
  paused?: boolean;
  /** Set by cancelBatch; processBatch exits on the next pull. */
  cancelled?: boolean;
}

export type UploadStatus =
  | 'pending'
  | 'pending-needs-file'
  | 'extracting'
  | 'summarizing'
  | 'indexing'
  | 'uploading'
  | 'completed'
  | 'failed'
  | 'failed-permanent'
  | 'cancelled'
  | 'skipped-duplicate';

export interface UploadQueueRow {
  id: string;
  batchId: string;
  fileName: string;
  relativePath: string;
  sizeBytes: number;
  contentSha256?: string;
  mimeType: string;
  status: UploadStatus;
  attempts: number;
  lastError?: string;
  retryable?: boolean;
  docId?: string;
  rawStoragePath?: string;
  rawBytesVerified?: boolean;
  enrichmentStatus?: 'complete' | 'deferred' | 'failed';
  enrichmentDeferredStages?: string[];
  chunkProgress?: { completedChunks: number; totalChunks: number };
  enqueuedAt: string;
  startedAt?: string;
  completedAt?: string;
}

export interface ProcessRowResult {
  docId?: string;
  rawStoragePath?: string;
  rawBytesVerified?: boolean;
  enrichmentStatus?: 'complete' | 'deferred' | 'failed';
  enrichmentDeferredStages?: string[];
}

/** Per-row callback the queue invokes inside processBatch's worker pool.
 *  Returns either a successful terminal status with optional docId, or
 *  throws — the queue handles retry/permanent-failure stamping. */
export type ProcessRowFn = (
  row: UploadQueueRow,
  file: File,
  helpers: {
    setStatus: (s: UploadStatus) => Promise<void>;
    updateProgress?: (patch: Partial<UploadQueueRow>) => Promise<void>;
    signal?: AbortSignal;
  },
) => Promise<ProcessRowResult>;

export interface ProcessBatchOpts {
  processRow: ProcessRowFn;
  /** Default 4. */
  concurrency?: number;
  /** Max attempts before flipping to 'failed-permanent'. Default 3. */
  maxAttempts?: number;
}

/* ── KvStore — pluggable backend ───────────────────────────────────── */

export interface KvStore {
  put<T>(store: 'batches' | 'rows', key: string, value: T): Promise<void>;
  get<T>(store: 'batches' | 'rows', key: string): Promise<T | null>;
  delete(store: 'batches' | 'rows', key: string): Promise<void>;
  list<T>(store: 'batches' | 'rows'): Promise<T[]>;
}

/** Test-friendly synchronous-ish store backed by JS Maps. Promise-wrapped
 *  so callers can swap to IDB without changing call sites. */
export class MemoryKvStore implements KvStore {
  private readonly stores: Record<'batches' | 'rows', Map<string, unknown>> = {
    batches: new Map(),
    rows: new Map(),
  };
  async put<T>(store: 'batches' | 'rows', key: string, value: T): Promise<void> {
    // Deep-clone on write to mirror IDB's structured-clone snapshot semantics.
    this.stores[store].set(key, structuredClone(value));
  }
  async get<T>(store: 'batches' | 'rows', key: string): Promise<T | null> {
    const v = this.stores[store].get(key);
    return v === undefined ? null : (structuredClone(v) as T);
  }
  async delete(store: 'batches' | 'rows', key: string): Promise<void> {
    this.stores[store].delete(key);
  }
  async list<T>(store: 'batches' | 'rows'): Promise<T[]> {
    return Array.from(this.stores[store].values()).map(v => structuredClone(v) as T);
  }
}

/** Hand-rolled IDB wrapper — promise-friendly, single connection, lazy init. */
export class IdbKvStore implements KvStore {
  private dbPromise: Promise<IDBDatabase> | null = null;
  constructor(private readonly dbName = 'deepatomic-upload-queue', private readonly version = 1) {}

  private openDb(): Promise<IDBDatabase> {
    if (this.dbPromise) return this.dbPromise;
    this.dbPromise = new Promise((resolve, reject) => {
      const req = indexedDB.open(this.dbName, this.version);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains('batches')) db.createObjectStore('batches', { keyPath: 'id' });
        if (!db.objectStoreNames.contains('rows')) db.createObjectStore('rows', { keyPath: 'id' });
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error ?? new Error('IDB open failed'));
    });
    return this.dbPromise;
  }

  private tx(store: 'batches' | 'rows', mode: IDBTransactionMode): Promise<IDBObjectStore> {
    return this.openDb().then(db => db.transaction(store, mode).objectStore(store));
  }

  put<T>(store: 'batches' | 'rows', key: string, value: T): Promise<void> {
    return this.tx(store, 'readwrite').then(s => new Promise<void>((resolve, reject) => {
      const r = s.put({ ...(value as object), id: key });
      r.onsuccess = () => resolve();
      r.onerror = () => reject(r.error ?? new Error('IDB put failed'));
    }));
  }

  get<T>(store: 'batches' | 'rows', key: string): Promise<T | null> {
    return this.tx(store, 'readonly').then(s => new Promise<T | null>((resolve, reject) => {
      const r = s.get(key);
      r.onsuccess = () => resolve((r.result ?? null) as T | null);
      r.onerror = () => reject(r.error ?? new Error('IDB get failed'));
    }));
  }

  delete(store: 'batches' | 'rows', key: string): Promise<void> {
    return this.tx(store, 'readwrite').then(s => new Promise<void>((resolve, reject) => {
      const r = s.delete(key);
      r.onsuccess = () => resolve();
      r.onerror = () => reject(r.error ?? new Error('IDB delete failed'));
    }));
  }

  list<T>(store: 'batches' | 'rows'): Promise<T[]> {
    return this.tx(store, 'readonly').then(s => new Promise<T[]>((resolve, reject) => {
      const r = s.getAll();
      r.onsuccess = () => resolve((r.result ?? []) as T[]);
      r.onerror = () => reject(r.error ?? new Error('IDB list failed'));
    }));
  }
}

/* ── SHA-256 dedup helper ──────────────────────────────────────────── */

/** Default crypto.subtle-backed SHA-256. Tests inject a stub via the
 *  UploadQueue constructor opts so we don't depend on jsdom shipping
 *  a working SubtleCrypto in every Node version. */
export async function defaultSha256(file: Pick<File, 'arrayBuffer'>): Promise<string> {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

/* ── UploadQueue ────────────────────────────────────────────────────── */

export interface UploadQueueOpts {
  store?: KvStore;
  /** Override the SHA hash function for tests. */
  sha256?: (file: Pick<File, 'arrayBuffer'>) => Promise<string>;
}

export class UploadQueue {
  private readonly store: KvStore;
  private readonly sha256: (file: Pick<File, 'arrayBuffer'>) => Promise<string>;
  /** In-memory only — File handles cannot be persisted to IndexedDB
   *  across reloads (browser security). On resume, rows whose handle is
   *  absent flip to 'pending-needs-file'. */
  private readonly fileHandles = new Map<string, File>();
  /** Per-row AbortController for active in-flight rows so cancelBatch/cancelRow aborts immediately. */
  private readonly rowControllers = new Map<string, AbortController>();

  constructor(opts: UploadQueueOpts = {}) {
    this.store = opts.store ?? new MemoryKvStore();
    this.sha256 = opts.sha256 ?? defaultSha256;
  }

  /* ── Core CRUD ──────────────────────────────────────────────────── */

  async enqueue(
    batch: Omit<UploadBatch, 'totalFiles' | 'createdAt'> & { createdAt?: string },
    files: File[],
  ): Promise<UploadQueueRow[]> {
    if (files.length === 0) return [];

    const createdAt = batch.createdAt ?? new Date().toISOString();
    const persistedBatch: UploadBatch = {
      id: batch.id,
      siteId: batch.siteId,
      reportId: batch.reportId,
      label: batch.label,
      totalFiles: files.length,
      createdAt,
    };
    await this.store.put('batches', persistedBatch.id, persistedBatch);

    // Compute SHA + check for prior-completed dedup hits.
    const allRows = await this.store.list<UploadQueueRow>('rows');
    const completedBySha = new Map<string, UploadQueueRow>();
    for (const r of allRows) {
      if (r.status === 'completed' && r.contentSha256) {
        completedBySha.set(r.contentSha256, r);
      }
    }

    const rows: UploadQueueRow[] = [];
    for (const f of files) {
      const id = `urow-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      let sha: string | undefined;
      try {
        sha = await this.sha256(f);
      } catch {
        sha = undefined;
      }
      const existingCompleted = sha != null ? completedBySha.get(sha) : undefined;
      const isDuplicate = Boolean(existingCompleted);
      const row: UploadQueueRow = {
        id,
        batchId: batch.id,
        fileName: f.name,
        relativePath: (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name,
        sizeBytes: f.size,
        contentSha256: sha,
        mimeType: f.type || 'application/octet-stream',
        status: isDuplicate ? 'skipped-duplicate' : 'pending',
        attempts: 0,
        ...(existingCompleted?.docId ? { docId: existingCompleted.docId } : {}),
        ...(existingCompleted?.rawStoragePath ? { rawStoragePath: existingCompleted.rawStoragePath } : {}),
        enqueuedAt: new Date().toISOString(),
      };
      this.fileHandles.set(id, f);
      await this.store.put('rows', id, row);
      rows.push(row);
    }
    return rows;
  }

  async listIncompleteBatches(): Promise<UploadBatch[]> {
    const [batches, rows] = await Promise.all([
      this.store.list<UploadBatch>('batches'),
      this.store.list<UploadQueueRow>('rows'),
    ]);
    const incompleteBatchIds = new Set(
      rows
        .filter(r => !isTerminal(r.status))
        .map(r => r.batchId),
    );
    return batches.filter(b => incompleteBatchIds.has(b.id));
  }

  async listRows(batchId: string): Promise<UploadQueueRow[]> {
    const all = await this.store.list<UploadQueueRow>('rows');
    return all
      .filter(r => r.batchId === batchId)
      .sort((a, b) => a.enqueuedAt.localeCompare(b.enqueuedAt));
  }

  async updateRow(id: string, patch: Partial<UploadQueueRow>): Promise<void> {
    const existing = await this.store.get<UploadQueueRow>('rows', id);
    if (!existing) throw new Error(`Row ${id} not found`);
    await this.store.put('rows', id, { ...existing, ...patch, id });
  }

  async nextPending(batchId: string): Promise<UploadQueueRow | null> {
    const batch = await this.store.get<UploadBatch>('batches', batchId);
    if (!batch || batch.cancelled || batch.paused) return null;
    const rows = await this.listRows(batchId);
    return rows.find(r => r.status === 'pending') ?? null;
  }

  async pauseBatch(batchId: string): Promise<void> {
    const b = await this.store.get<UploadBatch>('batches', batchId);
    if (!b) return;
    await this.store.put('batches', batchId, { ...b, paused: true });
  }

  async resumeBatch(batchId: string): Promise<void> {
    const b = await this.store.get<UploadBatch>('batches', batchId);
    if (!b) return;
    await this.store.put('batches', batchId, { ...b, paused: false });
    // Flip any rows that have been re-bound with a File handle back from
    // 'pending-needs-file' or retryable 'failed' to 'pending'.
    const rows = await this.listRows(batchId);
    for (const r of rows) {
      if (
        (r.status === 'pending-needs-file' || r.status === 'failed' || r.status === 'extracting' || r.status === 'summarizing' || r.status === 'indexing' || r.status === 'uploading') &&
        this.hasFile(r.id)
      ) {
        await this.updateRow(r.id, { status: 'pending' });
      }
    }
  }

  async cancelBatch(batchId: string): Promise<void> {
    const b = await this.store.get<UploadBatch>('batches', batchId);
    if (!b) return;
    await this.store.put('batches', batchId, { ...b, cancelled: true });
    const rows = await this.listRows(batchId);
    for (const r of rows) {
      const ctrl = this.rowControllers.get(r.id);
      if (ctrl) {
        ctrl.abort(new Error('Batch cancelled'));
      }
    }
  }

  async cancelRow(rowId: string): Promise<void> {
    const ctrl = this.rowControllers.get(rowId);
    if (ctrl) {
      ctrl.abort(new Error('Row cancelled'));
    }
    const existing = await this.store.get<UploadQueueRow>('rows', rowId);
    if (existing && !isTerminal(existing.status)) {
      await this.updateRow(rowId, {
        status: 'cancelled',
        retryable: false,
        lastError: 'Cancelled by user',
        completedAt: new Date().toISOString(),
      });
    }
  }

  /* ── File-handle plumbing ───────────────────────────────────────── */

  /** Attach a freshly-selected File to a row whose handle was lost across
   *  a reload. Caller maps user-provided files back to rows by name (or
   *  by the explicit row.id when the UX surfaces individual file pickers). */
  registerFile(rowId: string, file: File): void {
    this.fileHandles.set(rowId, file);
  }

  hasFile(rowId: string): boolean {
    return this.fileHandles.has(rowId);
  }

  /* ── processBatch — worker pool ─────────────────────────────────── */

  async processBatch(batchId: string, opts: ProcessBatchOpts): Promise<void> {
    const concurrency = opts.concurrency ?? 4;
    const maxAttempts = opts.maxAttempts ?? 3;

    // Mark every row whose File handle isn't in memory anymore. Done
    // up-front so `pending-needs-file` rows show in the UX immediately
    // rather than getting picked up and rejected one-by-one.
    const rows = await this.listRows(batchId);
    for (const row of rows) {
      if (
        (row.status === 'pending' ||
          row.status === 'extracting' ||
          row.status === 'summarizing' ||
          row.status === 'indexing' ||
          row.status === 'uploading') &&
        !this.hasFile(row.id)
      ) {
        await this.updateRow(row.id, { status: 'pending-needs-file', retryable: true });
      } else if (
        (row.status === 'pending-needs-file' ||
          row.status === 'extracting' ||
          row.status === 'summarizing' ||
          row.status === 'indexing' ||
          row.status === 'uploading') &&
        this.hasFile(row.id)
      ) {
        await this.updateRow(row.id, { status: 'pending' });
      }
    }

    const worker = async (): Promise<void> => {
      while (true) {
        const next = await this.nextPending(batchId);
        if (!next) return;
        await this.runOne(next, opts.processRow, maxAttempts);
      }
    };

    const workers = Array.from({ length: concurrency }, () => worker());
    await Promise.all(workers);
  }

  /** Single-row execution with retry-on-failure semantics. */
  private async runOne(
    row: UploadQueueRow,
    processRow: ProcessRowFn,
    maxAttempts: number,
  ): Promise<void> {
    const file = this.fileHandles.get(row.id);
    if (!file) {
      // Lost between markers. Race with the user re-selecting; reflect.
      await this.updateRow(row.id, { status: 'pending-needs-file', retryable: true });
      return;
    }
    const startedAt = new Date().toISOString();
    const newAttempts = row.attempts + 1;
    const controller = new AbortController();
    this.rowControllers.set(row.id, controller);
    await this.updateRow(row.id, {
      startedAt,
      attempts: newAttempts,
      status: 'extracting',
      lastError: undefined,
    });
    try {
      const currentRow = (await this.store.get<UploadQueueRow>('rows', row.id)) ?? {
        ...row,
        startedAt,
        attempts: newAttempts,
        status: 'extracting',
      };
      const result = await processRow(currentRow, file, {
        setStatus: (s) => this.updateRow(row.id, { status: s }),
        updateProgress: (patch) => this.updateRow(row.id, patch),
        signal: controller.signal,
      });
      await this.updateRow(row.id, {
        status: 'completed',
        retryable: false,
        completedAt: new Date().toISOString(),
        docId: result.docId ?? currentRow.docId,
        ...(result.rawStoragePath ? { rawStoragePath: result.rawStoragePath } : {}),
        ...(typeof result.rawBytesVerified === 'boolean' ? { rawBytesVerified: result.rawBytesVerified } : {}),
        ...(result.enrichmentStatus ? { enrichmentStatus: result.enrichmentStatus } : {}),
        ...(result.enrichmentDeferredStages ? { enrichmentDeferredStages: result.enrichmentDeferredStages } : {}),
      });
    } catch (e) {
      const batchAfter = await this.store.get<UploadBatch>('batches', row.batchId);
      const rowAfter = await this.store.get<UploadQueueRow>('rows', row.id);
      if (rowAfter?.status === 'cancelled' || (controller.signal.aborted && batchAfter?.cancelled)) {
        await this.updateRow(row.id, {
          status: 'cancelled',
          retryable: false,
          lastError: 'Cancelled by user',
          completedAt: new Date().toISOString(),
        });
        return;
      }
      const err = (e as Error)?.message ?? String(e);
      const explicitlyNonRetryable =
        (e as { retryable?: boolean })?.retryable === false ||
        /exceeds.*raw-storage limit|exceeds.*raw-file limit|Not signed in|Invalid storage path/i.test(err);
      const terminal = explicitlyNonRetryable || newAttempts >= maxAttempts;
      await this.updateRow(row.id, {
        status: terminal ? 'failed-permanent' : 'failed',
        retryable: !terminal,
        lastError: err.slice(0, 500),
        completedAt: terminal ? new Date().toISOString() : undefined,
      });
      if (!terminal) {
        await this.updateRow(row.id, { status: 'pending', retryable: true });
      }
    } finally {
      this.rowControllers.delete(row.id);
    }
  }
}

/* ── Helpers + module-singleton ────────────────────────────────────── */

function isTerminal(status: UploadStatus): boolean {
  return (
    status === 'completed' ||
    status === 'failed-permanent' ||
    status === 'cancelled' ||
    status === 'skipped-duplicate'
  );
}

let _singleton: UploadQueue | null = null;
/** Returns the process-wide singleton. Defaults to IndexedDB in the browser
 *  and an in-memory store everywhere else (Node / vitest / replay scripts). */
export function getUploadQueue(): UploadQueue {
  if (_singleton) return _singleton;
  const isBrowser = typeof indexedDB !== 'undefined';
  _singleton = new UploadQueue({ store: isBrowser ? new IdbKvStore() : new MemoryKvStore() });
  return _singleton;
}
